// Subscriptions of activated self-install trackers (task 6, phase 3). Default view: the ones
// that need attention (expiring, in grace, or with no active subscription). Units with no
// active subscription are hidden in the customer app; staff renew them or remove them by hand.
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { SubFilter, subscriptions, SubscriptionRow, SubStatus } from '../api';
import { RenewForm, SubChip, UntilText } from '../components/Subscription';
import { ago, dateTime, dayOnly, SUB_LABEL } from '../format';
import { DataGrid } from '../components/DataGrid';

const TABS: Array<[SubFilter, string]> = [
  ['attention', 'Needs attention'], ['expiring', 'Expiring soon'], ['grace', 'Grace period'],
  ['expired', 'No active subscription'], ['active', 'Active'], ['all', 'All'],
];

export function Subscriptions() {
  const [params, setParams] = useSearchParams();
  const filter = (params.get('show') as SubFilter) ?? 'attention';
  const [data, setData] = useState<Awaited<ReturnType<typeof subscriptions.list>> | null>(null);
  const [error, setError] = useState('');
  const [renewing, setRenewing] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  const load = useCallback(() => { subscriptions.list(filter).then(setData).catch((e) => setError(e.message)); }, [filter]);
  useEffect(load, [load]);

  const count = (f: SubFilter) => {
    if (!data) return null;
    const c = data.counts;
    if (f === 'all') return c.active + c.expiring + c.grace + c.expired;
    if (f === 'attention') return c.expiring + c.grace + c.expired + (c.closedActive ?? 0);
    return c[f as SubStatus];
  };
  const unitLink = (r: SubscriptionRow) => (r.vehicle ? `/vehicles/${r.vehicle.id}` : `/trackers/${r.trackerId}`);

  return (
    <div className="page wide">
      <div className="page-head"><h1>Subscriptions</h1></div>
      <p className="muted small">Self-install trackers run for a year from activation. {data && <>Expiring soon = less than {data.expiringDays} days left. After the end there are {data.graceDays} days' grace; then the vehicle is hidden in the customer's app (still recorded) until it is renewed or removed.</>}</p>
      <div className="subtabs" role="tablist">
        {TABS.map(([f, label]) => (
          <button key={f} role="tab" aria-selected={f === filter} className={f === filter ? 'on' : ''}
            onClick={() => { setRenewing(null); setNotice(''); setParams(f === 'attention' ? {} : { show: f }, { replace: true }); }}>
            {label}{count(f) !== null && <span className="subtabs-n">{count(f)}</span>}
          </button>
        ))}
      </div>
      {notice && <p className="notice" role="status">{notice}</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {data && (
        <DataGrid id="subscriptions" rows={data.trackers} rowKey={(r) => r.trackerId} noun={['unit', 'units']}
          empty={filter === 'attention' ? 'Nothing needs attention.' : 'None.'} defaultSort={{ key: 'until', dir: 1 }}
          after={(r) => renewing === r.trackerId && (
            <RenewPanel row={r} onCancel={() => setRenewing(null)} onDone={(msg) => { setRenewing(null); setNotice(`${r.vehicle?.name ?? r.imei}: ${msg}`); load(); }} />
          )}
          columns={[
            { key: 'unit', label: 'Unit', always: true, value: (r) => r.vehicle?.name ?? r.imei, cell: (r) => <Link to={unitLink(r)}><strong>{r.vehicle?.name ?? r.imei}</strong></Link> },
            { key: 'reg', label: 'Registration', value: (r) => r.vehicle?.registration ?? null, nowrap: true },
            { key: 'imei', label: 'IMEI', value: (r) => r.imei, cell: (r) => <span className="mono">{r.imei}</span> },
            { key: 'customer', label: 'Customer', value: (r) => r.customer?.name ?? null,
              cell: (r) => (r.customer ? <Link to={`/customers/${r.customer.id}`}>{r.customer.name}</Link> : <span className="muted">–</span>) },
            { key: 'until', label: 'Paid until', value: (r) => r.subscription?.paidUntil ?? null,
              sortBy: (r) => (r.subscription?.paidUntil ? Date.parse(r.subscription.paidUntil) : null),
              csv: (r) => (r.subscription?.paidUntil ? dayOnly(r.subscription.paidUntil) : null),
              cell: (r) => (r.subscription ? <UntilText sub={r.subscription} /> : <span className="muted">–</span>) },
            { key: 'status', label: 'Status', value: (r) => (r.subscription ? SUB_LABEL[r.subscription.status] : null),
              cell: (r) => (
                <>
                  {r.subscription ? <SubChip status={r.subscription.status} /> : <span className="muted">–</span>}
                  {r.accountClosedAt && <span className="muted small" title={`The customer deleted its last app login on ${dateTime(r.accountClosedAt)}. Remove the tracker.`}> · Account deleted</span>}
                </>
              ), nowrap: true },
            { key: 'last', label: 'Last data', value: (r) => r.lastReceived, sortBy: (r) => (r.lastReceived ? Date.parse(r.lastReceived) : null),
              csv: (r) => (r.lastReceived ? dateTime(r.lastReceived) : null),
              cell: (r) => (r.lastReceived ? <span title={dateTime(r.lastReceived)}>{ago(r.lastReceived)}</span> : <span className="muted">never</span>), nowrap: true },
            { key: 'renew', label: '', nosort: true, nocsv: true, always: true, value: () => null,
              cell: (r) => (renewing !== r.trackerId && r.customer ? <button className="link" onClick={() => { setNotice(''); setRenewing(r.trackerId); }}>Renew</button> : null) },
          ]} />
      )}
    </div>
  );
}

// The renew form needs where the next period starts, which comes with the tracker's periods.
function RenewPanel({ row, onCancel, onDone }: { row: SubscriptionRow; onCancel: () => void; onDone: (msg: string) => void }) {
  const [next, setNext] = useState<{ nextStart: string; paidUntil: string | null } | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { subscriptions.ofTracker(row.trackerId).then((d) => setNext({ nextStart: d.nextStart, paidUntil: d.subscription?.paidUntil ?? null })).catch((e) => setError(e.message)); }, [row.trackerId]);
  if (error) return <p className="error">{error}</p>;
  if (!next) return <p className="muted small">Loading…</p>;
  return <RenewForm trackerId={row.trackerId} nextStart={next.nextStart} paidUntil={next.paidUntil} onCancel={onCancel} onDone={onDone} />;
}
